<script setup lang="ts">
import { hexAlpha } from '@/utils/levels';

// Layout de celular: duas abas numa coluna de 430px. O desktop tem uma
// estrutura bem diferente (página única de scroll, com pódio e comparativo),
// por isso vive em LevelsDesktopView.vue em vez de virar breakpoints daqui.
const {
  xp,
  level,
  isMaxLevel,
  tier,
  highlightTierIndex,
  playerStepIndex,
  levelProgress,
  xpToNextLevel,
  currentLevelLabel,
  nextLevelLabel,
  playerName,
  playerAvatar,
  viewIndex,
  viewTier,
  viewState,
  prevTier,
  nextTier,
  goToTier,
  stepTier,
  viewBenefits,
  levelXpTable,
} = useLevelsProgress();

type LevelsTab = 'tiers' | 'progress';

const tab = ref<LevelsTab>('tiers');

const tabs: { key: LevelsTab; label: string }[] = [
  { key: 'tiers', label: 'Níveis' },
  { key: 'progress', label: 'Progresso' },
];

/** Cartão-ponte no Diamante: é onde a escala aberta acaba e o VIP começa. */
const showVipGate = computed(
  () => tab.value === 'progress' && viewTier.value.key === 'diamante',
);

/**
 * Fagulhas subindo atrás do herói. Posições derivadas do índice (não de
 * `Math.random`) para o fundo não "pular" a cada re-render.
 */
const particles = computed(() =>
  Array.from({ length: 24 }, (_, i) => ({
    left: `${(i * 37 + 11) % 100}%`,
    size: `${2 + ((i * 5) % 3)}px`,
    duration: `${9 + ((i * 7) % 8)}s`,
    delay: `-${(i * 13) % 11}s`,
    color: i % 3 === 0 ? viewTier.value.accent : viewTier.value.color,
  })),
);

/** O fundo da página acompanha o elo em foco. */
const pageBackground = computed(() => {
  const color =
    tab.value === 'progress' ? viewTier.value.color : tier.value.color;

  return `radial-gradient(76% 50% at 50% 0%,${hexAlpha(color, 0.22)} 0%,#020716 46%,#020716 76%)`;
});

/** Clicar num elo na aba "Níveis" abre esse elo na aba "Progresso". */
function openTierProgress(index: number) {
  goToTier(index);
  tab.value = 'progress';
}

/**
 * A aba "Níveis" é longa (14 cartões), então quem clica num elo lá embaixo cai
 * no meio da aba "Progresso" — que tem só o herói daquele elo, já rolado pra
 * fora da tela. Toda troca de aba volta pro topo.
 *
 * `nextTick` porque a altura da página muda junto com a aba: rolar antes do
 * novo conteúdo montar deixa o scroll num offset que não existe mais.
 */
watch(tab, async () => {
  await nextTick();

  scrollToTop(0, false);
});
</script>

<template>
  <div
    class="relative flex w-full justify-center"
    :style="{ background: pageBackground }"
  >
    <div
      class="relative w-full max-w-[430px] overflow-x-hidden pb-8 text-white"
    >
      <!-- Aviso de fase beta: fica acima das abas, então vale pras duas. -->
      <LevelsBetaNotice class="relative z-[3] mx-[9px] mt-3.5" />

      <!-- Abas -->
      <div class="relative z-[3] mt-3.5 flex gap-2 px-[9px]">
        <button
          v-for="item in tabs"
          :key="item.key"
          class="min-w-0 flex-1 truncate rounded-full px-2.5 py-[11px] text-[13.5px] font-medium leading-[17px]"
          :style="
            tab === item.key
              ? {
                  background:
                    'linear-gradient(160deg,#000A6E 0%,#0A0AC0 34%,#2A2AFF 68%,#1414E0 100%)',
                  color: '#fff',
                  boxShadow: 'inset 0 0 0 1.5px #3A3AFF',
                }
              : {
                  background: 'rgba(255,255,255,.045)',
                  color: 'rgba(255,255,255,.62)',
                  boxShadow: 'inset 0 0 0 1px rgba(255,255,255,.07)',
                }
          "
          type="button"
          @click="tab = item.key"
        >
          {{ item.label }}
        </button>
      </div>

      <!-- Aba "Níveis": cartão do jogador + a escala inteira -->
      <template v-if="tab === 'tiers'">
        <div class="relative z-[4] px-[9px] pt-[34px]">
          <LevelsPlayerCard
            :avatar="playerAvatar"
            :is-max-level="isMaxLevel"
            :level-label="currentLevelLabel"
            :next-level-label="nextLevelLabel"
            :player-name="playerName"
            :progress="levelProgress"
            :tier="tier"
            :xp="xp"
            :xp-to-next="xpToNextLevel"
          />
        </div>

        <LevelsAllTiers
          :level="level"
          :level-xp-table="levelXpTable"
          :player-tier-index="highlightTierIndex"
          @select-tier="openTierProgress"
        />
      </template>

      <!-- Aba "Progresso": herói do elo + benefícios -->
      <template v-else>
        <!-- Fagulhas -->
        <div
          aria-hidden="true"
          class="pointer-events-none absolute left-0 right-0 top-0 z-[1] h-[300px] overflow-hidden"
          style="
            mask-image: linear-gradient(
              180deg,
              #000 0%,
              #000 58%,
              transparent 100%
            );
            -webkit-mask-image: linear-gradient(
              180deg,
              #000 0%,
              #000 58%,
              transparent 100%
            );
          "
        >
          <span
            v-for="(spark, index) in particles"
            :key="index"
            class="levels-drift absolute -bottom-2.5 rounded-full"
            :style="{
              left: spark.left,
              width: spark.size,
              height: spark.size,
              background: spark.color,
              boxShadow: `0 0 6px ${spark.color}`,
              animationDuration: spark.duration,
              animationDelay: spark.delay,
            }"
          ></span>
        </div>

        <div class="relative z-[2] flex flex-col gap-[15px] pb-8 pt-3.5">
          <button
            v-if="showVipGate"
            class="relative -mb-3 mx-[9px] flex items-center gap-[11px] overflow-hidden rounded-xl p-[13px] text-left"
            :style="{
              background: `linear-gradient(112deg,${hexAlpha(viewTier.color, 0.16)},rgba(5,12,34,.5))`,
              boxShadow: `inset 0 0 0 1px ${hexAlpha(viewTier.color, 0.4)}`,
            }"
            type="button"
            @click="stepTier(1)"
          >
            <span
              class="h-9 w-9 flex-none bg-contain bg-center bg-no-repeat"
              :style="{
                backgroundImage: `url(${nextTier?.badge})`,
                filter: 'drop-shadow(0 0 14px rgba(255,255,255,.4))',
              }"
            ></span>
            <span class="relative flex min-w-0 flex-1 flex-col gap-px">
              <span
                class="text-[13px] font-bold leading-[17px] tracking-[-0.01em] text-white"
              >
                Depois do Diamante começa o VIP
              </span>
              <span
                class="text-[11px] font-normal leading-[15px] text-white/[0.66]"
              >
                7 níveis VIP 0 a VIP 6, por convite. Sem tabela: tudo negociado
                com seu gerente.
              </span>
            </span>
            <LevelsNavArrow
              class="relative h-4 w-4 flex-none text-white/75"
              direction="next"
            />
          </button>

          <LevelsTierHero
            :next-tier="nextTier"
            :player-level-label="currentLevelLabel"
            :player-tier-index="playerStepIndex"
            :prev-tier="prevTier"
            :state="viewState"
            :tier="viewTier"
            :view-index="viewIndex"
            @step="stepTier"
          />

          <LevelsBenefitList
            :benefits="viewBenefits"
            class="mt-[18px]"
            :pitch="viewTier.pitch"
            :player-level="level"
            :state="viewState"
            :tier="viewTier"
          />

          <!-- Dúvidas frequentes fecham a aba, como no desktop — pintadas com
               o elo do JOGADOR (tier), não com o elo em foco no swiper. -->
          <LevelsFaqAccordion
            class="px-[9px] pb-9 pt-6 lg:px-[13px]"
            :tier="tier"
          />
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="postcss">
@keyframes levelsDrift {
  0% {
    transform: translateY(0);
    opacity: 0;
  }
  18% {
    opacity: 0.55;
  }
  100% {
    transform: translateY(-400px) translateX(14px);
    opacity: 0;
  }
}

.levels-drift {
  animation-name: levelsDrift;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
}
</style>
